<template>
  <aside
    class="sidebar"
    :class="{ 'sidebar-open': isMobileOpen }"
    @click.stop
  >
    <div class="sidebar-header">
      <div class="logo">
        <svg class="logo-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 20h5v-5a2 2 0 012-2V4a2 2 0 00-2-2H4a2 2 0 00-2 2v10a2 2 0 002 2h5M9 16h6V8H9v8zm0 0v4m0-4h6m-3-4V8m0 4v4m0 0h6m-6 0h-6M10 9l1 1h2l1-1m-4 5h8"></path>
        </svg>
        <div class="logo-text">
          <h1 class="logo-title">AGRO NOVAMED</h1>
          <p class="logo-subtitle">Gestion de Ferme</p>
        </div>
      </div>
    </div>

    <nav class="sidebar-nav">
      <router-link
        v-for="item in filteredMenuItems"
        :key="item.path"
        :to="item.path"
        class="nav-item"
        :class="{ active: $route.path === item.path }"
        @click="handleNavClick"
      >
        <svg class="nav-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" :d="item.icon"></path>
        </svg>
        <span class="nav-label">{{ item.label }}</span>
      </router-link>
    </nav>

    <div class="sidebar-footer">
      <div class="user-info">
        <div class="user-avatar">
          {{ userInitials }}
        </div>
        <div class="user-details">
          <p class="user-name">{{ authStore.user?.nom || 'Utilisateur' }}</p>
          <p class="user-email">{{ authStore.user?.email || '' }}</p>
        </div>
      </div>
      <button @click="handleLogout" class="logout-btn">
        <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"></path>
        </svg>
        <span>Se déconnecter</span>
      </button>
    </div>
  </aside>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { useRouter } from 'vue-router'
import { useAuthStore } from '@/stores/auth'

interface Props {
  isMobileOpen?: boolean
}

withDefaults(defineProps<Props>(), {
  isMobileOpen: false,
})

const emit = defineEmits<{
  close: []
}>()

const router = useRouter()
const authStore = useAuthStore()

const menuItems = [
  { 
    path: '/dashboard', 
    label: 'Tableau de bord', 
    icon: 'M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6'
  },
  { 
    path: '/bandes', 
    label: 'Bandes & Productions', 
    icon: 'M12 8c1.657 0 3 .895 3 2s-1.343 2-3 2h-1a2 2 0 00-2 2v3M9 20h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2z'
  },
  { 
    path: '/clients', 
    label: 'Clients & Commandes', 
    icon: 'M17 20h5v-5a2 2 0 012-2V4a2 2 0 00-2-2H4a2 2 0 00-2 2v10a2 2 0 002 2h5M9 16h6V8H9v8zm0 0v4m0-4h6m-3-4V8m0 4v4m0 0h6m-6 0h-6M10 9l1 1h2l1-1m-4 5h8'
  },
  { 
    path: '/finances', 
    label: 'Comptabilité & Finances', 
    icon: 'M14 10h4.764a2 2 0 011.789 2.894l-3.5 7A2 2 0 0115.263 21H12M4 7V4a1 1 0 011-1h14a1 1 0 011 1v3M4 7h16M4 7l-2 5v11h20V12l-2-5M10 11V7'
  },
  { 
    path: '/stocks', 
    label: 'Gestion de Stocks', 
    icon: 'M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7C4 4.79 7.582 3 12 3s8 1.79 8 4M4 7h16'
  },
  { 
    path: '/inventaire', 
    label: 'Inventaires & Rapprochements', 
    icon: 'M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2'
  },
  { 
    path: '/rh', 
    label: 'Ressources Humaines', 
    icon: 'M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z'
  },
  { 
    path: '/prophylactique', 
    label: 'Calendrier Prophylactique', 
    icon: 'M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z'
  },
  { 
    path: '/configuration', 
    label: 'Configuration', 
    icon: 'M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z M15 12a3 3 0 11-6 0 3 3 0 016 0z'
  },
]

// Filtrer les items du menu selon le rôle de l'utilisateur
const filteredMenuItems = computed(() => {
  const userRole = authStore.user?.role?.toLowerCase() || ''
  const isAdmin = userRole === 'admin'
  
  return menuItems.filter(item => {
    // Masquer Configuration si l'utilisateur n'est pas admin
    if (item.path === '/configuration' && !isAdmin) {
      return false
    }
    return true
  })
})

const userInitials = computed(() => {
  const name = authStore.user?.nom || 'U'
  return name
    .split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2)
})

const handleNavClick = () => {
  emit('close')
}

const handleLogout = () => {
  authStore.logout()
  router.push('/auth/login')
}
</script>

<style scoped>
.sidebar {
  @apply fixed top-0 left-0 h-full w-70 shadow-xl z-50 flex flex-col;
  @apply transform -translate-x-full transition-transform duration-300 ease-in-out;
  @apply lg:translate-x-0;
  background: linear-gradient(135deg, #d1fae5 0%, #a7f3d0 100%);
  backdrop-filter: blur(20px);
  border-right: 1px solid rgba(16, 185, 129, 0.2);

  @media (min-width: 1024px) {
    width: 280px;
  }
}

.sidebar-open {
  @apply translate-x-0;
}

.sidebar-header {
  @apply p-6 border-b;
  border-color: rgba(16, 185, 129, 0.2);
  background: linear-gradient(135deg, #d1fae5 0%, #a7f3d0 100%);
}

.logo {
  @apply flex items-center gap-3;
}

.logo-icon {
  @apply w-7 h-7 flex-shrink-0;
  color: #059669;
}

.logo-text {
  @apply flex-1;
}

.logo-title {
  @apply text-lg font-bold;
  color: #047857;
}

.logo-subtitle {
  @apply text-xs;
  color: #059669;
}

.sidebar-nav {
  @apply flex-1 overflow-y-auto p-4 space-y-2;
}

.nav-item {
  @apply flex items-center gap-3 px-4 py-3 rounded-lg;
  @apply transition-colors duration-200;
  color: #047857;
  @apply hover:bg-white/60;
}

.nav-item.active {
  background: rgba(255, 255, 255, 0.8);
  @apply text-emerald-700 font-medium;
  border-left: 3px solid #10b981;
  box-shadow: 0 2px 8px -1px rgba(16, 185, 129, 0.2);
}

.nav-icon {
  @apply w-5 h-5 flex-shrink-0;
  color: #059669;
}

.nav-item.active .nav-icon {
  color: #10b981;
}

.nav-label {
  @apply text-sm;
}

.sidebar-footer {
  @apply p-4 border-t space-y-3;
  border-color: rgba(16, 185, 129, 0.2);
  background: linear-gradient(to top, transparent 0%, rgba(209, 250, 229, 0.5) 100%);
}

.user-info {
  @apply flex items-center gap-3 px-2;
}

.user-avatar {
  @apply w-10 h-10 rounded-full text-white;
  @apply flex items-center justify-center font-semibold text-sm;
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  border: 2px solid rgba(255, 255, 255, 0.8);
}

.user-details {
  @apply flex-1 min-w-0;
}

.user-name {
  @apply text-sm font-medium truncate;
  color: #047857;
}

.user-email {
  @apply text-xs truncate;
  color: #059669;
}

.logout-btn {
  @apply w-full flex items-center gap-3 px-4 py-2 rounded-lg;
  @apply transition-colors text-sm font-medium;
  color: #dc2626;
  @apply hover:bg-red-50;
}
</style>

